import type { ReferenceImageItem } from '../api/referenceImages'
import {
  filteredPathsForGalleryMainTrieMerge,
  isUnderReferenceUploadSubtreeUiPath,
  LIBRARY_REFERENCE_UPLOAD_SEGMENT,
  normalizeLibraryUiFolderPath,
} from './libraryPaths'

export type FolderTreeNode = {
  path: string
  segment: string
  children: FolderTreeNode[]
  subtreeItemCount: number
}

type Trie = Map<string, Trie>

function insertSegments(trie: Trie, segments: string[]): void {
  if (segments.length === 0) return
  const [head, ...rest] = segments
  if (!trie.has(head)) trie.set(head, new Map())
  insertSegments(trie.get(head)!, rest)
}

function sortTree(nodes: FolderTreeNode[]) {
  nodes.sort((a, b) =>
    a.segment.localeCompare(b.segment, 'zh-CN', { sensitivity: 'base', numeric: true }),
  )
  for (const n of nodes) sortTree(n.children)
}

function trieToNodes(
  trie: Trie,
  parentPath: string,
  directCount: (path: string) => number,
): FolderTreeNode[] {
  const list: FolderTreeNode[] = []
  for (const [seg, childTrie] of trie) {
    const path = parentPath ? `${parentPath}/${seg}` : seg
    const children = trieToNodes(childTrie, path, directCount)
    let subtreeItemCount = directCount(path)
    for (const c of children) subtreeItemCount += c.subtreeItemCount
    list.push({
      path,
      segment: seg,
      children,
      subtreeItemCount,
    })
  }
  sortTree(list)
  return list
}

/** 各文件夹路径下直接文件数 */
function directCountsByPath(items: ReferenceImageItem[]): Map<string, number> {
  const m = new Map<string, number>()
  for (const it of items) {
    const fp = normalizeLibraryUiFolderPath(it.folderPath)
    m.set(fp, (m.get(fp) ?? 0) + 1)
  }
  return m
}

/**
 * 从参考图构造文件夹树（仅含出现过的路径分支）。
 * UI 中 path === '' 表示「图库根目录」（仅该层文件，非全局汇总）。
 */
export function folderTreeRoots(items: ReferenceImageItem[]): FolderTreeNode[] {
  const direct = directCountsByPath(items)
  const paths = new Set(direct.keys())
  const trie: Trie = new Map()
  for (const fp of paths) {
    const segs = fp.split('/').filter(Boolean)
    insertSegments(trie, segs)
  }
  const countAt = (p: string) => direct.get(p) ?? 0
  return trieToNodes(trie, '', countAt)
}

/** 遍历文件夹树，收集全部节点 path（不含虚拟「根」空串）。 */
export function collectFolderPathsFromTree(nodes: FolderTreeNode[]): Set<string> {
  const set = new Set<string>()
  const walk = (ns: FolderTreeNode[]) => {
    for (const n of ns) {
      set.add(n.path)
      walk(n.children)
    }
  }
  walk(nodes)
  return set
}

export function mergeFolderTreeRootsWithPaths(
  rootsFromItems: FolderTreeNode[],
  paths: string[],
): FolderTreeNode[] {
  const directCount = new Map<string, number>()
  const trie: Trie = new Map()

  const walk = (nodes: FolderTreeNode[]) => {
    nodes.forEach((node) => {
      directCount.set(node.path, Math.max(0, node.subtreeItemCount - node.children.reduce((sum, c) => sum + c.subtreeItemCount, 0)))
      insertSegments(trie, node.path.split('/').filter(Boolean))
      walk(node.children)
    })
  }
  walk(rootsFromItems)

  paths.forEach((p) => {
    const normalized = p.trim().replaceAll('\\', '/').replace(/\/+/g, '/').replace(/^\/+|\/+$/g, '')
    if (!normalized) return
    insertSegments(trie, normalized.split('/').filter(Boolean))
    if (!directCount.has(normalized)) directCount.set(normalized, 0)
  })

  const countAt = (p: string) => directCount.get(p) ?? 0
  return trieToNodes(trie, '', countAt)
}

/** 主图库 Trie 不包含参考上传子树条目（该行由 {@link buildLibrarySidebarTreeModel} 顶置） */
export function itemsOutsideReferenceUploadSubtree(items: ReferenceImageItem[]): ReferenceImageItem[] {
  return items.filter((it) => !isUnderReferenceUploadSubtreeUiPath(it.folderPath))
}

/**
 * 单列「上传的参考图」（与虚拟「图库根」并排）：对象键在该业务前缀下的目录树。
 * <p>
 * 该目录是「生图阶段上传的参考图」链路（本地/独立模块）。
 * 入库阶段落进图库的参考图副本（saved_references）不走这里，后端读取时已隐藏。
 */
export function buildReferenceUploadPeerRoot(items: ReferenceImageItem[]): FolderTreeNode {
  const REF = LIBRARY_REFERENCE_UPLOAD_SEGMENT
  const direct = new Map<string, number>()
  const trie: Trie = new Map()

  for (const it of items) {
    const fp = it.folderPath
    if (!isUnderReferenceUploadSubtreeUiPath(fp)) continue
    const unified = normalizeLibraryUiFolderPath(fp)
    const rel = unified === REF ? '' : unified.slice(REF.length + 1)
    const segs = rel.split('/').filter(Boolean)
    insertSegments(trie, segs)
    direct.set(unified, (direct.get(unified) ?? 0) + 1)
  }

  const countAt = (p: string) => direct.get(p) ?? 0

  function refTrieToNodes(trieNode: Trie, unifiedParentPath: string): FolderTreeNode[] {
    const list: FolderTreeNode[] = []
    for (const [seg, childTrie] of trieNode) {
      const path = `${unifiedParentPath}/${seg}`
      const children = refTrieToNodes(childTrie, path)
      let subtreeItemCount = countAt(path)
      for (const c of children) subtreeItemCount += c.subtreeItemCount
      list.push({
        path,
        segment: seg,
        children,
        subtreeItemCount,
      })
    }
    sortTree(list)
    return list
  }

  const children = refTrieToNodes(trie, REF)
  let subtreeItemCount = countAt(REF)
  for (const c of children) subtreeItemCount += c.subtreeItemCount
  return {
    path: REF,
    segment: REF,
    children,
    subtreeItemCount,
  }
}

/**
 * 侧栏数据结构：七牛图库子树 + 「上传的参考图」根（与虚拟「图库根」平级展示）。
 */
export type LibrarySidebarTreeModel = {
  cloudRoots: FolderTreeNode[]
  referenceUploadRoot: FolderTreeNode
}

export function buildLibrarySidebarTreeModel(
  items: ReferenceImageItem[],
  backendPaths: readonly string[],
): LibrarySidebarTreeModel {
  const cloudRoots = mergeFolderTreeRootsWithPaths(
    folderTreeRoots(itemsOutsideReferenceUploadSubtree(items)),
    filteredPathsForGalleryMainTrieMerge(backendPaths ?? []),
  )
  const referenceUploadRoot = buildReferenceUploadPeerRoot(items)
  return { cloudRoots, referenceUploadRoot }
}

export function collectFolderPathsFromSidebarModel(model: LibrarySidebarTreeModel): Set<string> {
  const out = collectFolderPathsFromTree(model.cloudRoots)
  for (const p of collectFolderPathsFromTree([model.referenceUploadRoot])) {
    out.add(p)
  }
  return out
}

/** selectedPath === '' 仅含「图库根」直下的文件（不含上传参考图子树）；否则按统一前缀匹配 */
export function filterItemsByFolderPath(
  items: ReferenceImageItem[],
  selectedPath: string,
): ReferenceImageItem[] {
  const sel = selectedPath.trim().replaceAll('\\', '/').replace(/^\/+|\/+$/g, '')
  return items.filter((it) => {
    const fp = it.folderPath
    const normFp = normalizeLibraryUiFolderPath(fp)
    if (sel === '') {
      if (isUnderReferenceUploadSubtreeUiPath(fp)) return false
      return fp === ''
    }
    const normSel = normalizeLibraryUiFolderPath(sel)
    return normFp === normSel || normFp.startsWith(`${normSel}/`)
  })
}

/** 上传到当前选中目录应使用的 folderPath */
export function uploadPathForSelectedFolder(selectedPath: string): string {
  return selectedPath.trim()
}
